import { jsx } from 'react/jsx-runtime';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import { forwardRef, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import deletionRegistry from '../utils/DeletionRegistry.js';
import useBooleanAccumulator from '../utils/useBooleanAccumulator.js';
import useGetterRef from '../utils/useGetterRef.js';
import useUnmount from '../utils/useUnmount.js';
import { useRenderWindowContext, RendererContext } from './contexts.js';
import useCamera from './modules/useCamera.js';

const DefaultProps = {
    background: [0.2, 0.3, 0.4],
    interactive: true,
    autoResetCamera: true,
};
var Renderer = forwardRef(function Renderer(props, fwdRef) {
    const renderWindow = useRenderWindowContext();
    const [modifiedRef, trackModified, resetModified] = useBooleanAccumulator();
    const [renRef, getRenderer] = useGetterRef(() => {
        const ren = vtkRenderer.newInstance();
        deletionRegistry.register(ren, () => ren.delete());
        return ren;
    });
    const { background = DefaultProps.background, interactive = DefaultProps.interactive, } = props;
    useEffect(() => {
        getRenderer().setBackground(background);
    }, [background, getRenderer]);
    useEffect(() => {
        getRenderer().setInteractive(interactive);
    }, [interactive, getRenderer]);
    useEffect(() => {
        const rw = renderWindow.get();
        deletionRegistry.incRefCount(rw);
        const renderer = getRenderer();
        rw.addRenderer(renderer);
        return () => {
            rw.removeRenderer(renderer);
            deletionRegistry.decRefCount(rw);
        };
    }, [renderWindow, getRenderer]);
    // --- camera --- //
    const { camera: cameraProps, autoResetCamera = DefaultProps.autoResetCamera, } = props;
    useCamera(getRenderer, cameraProps, trackModified);
    const resetCamera = useCallback((boundsToUse) => {
        getRenderer().resetCamera(boundsToUse);
    }, [getRenderer]);
    // --- cleanup --- //
    useUnmount(() => {
        if (renRef.current) {
            deletionRegistry.markForDeletion(renRef.current);
            renRef.current = null;
        }
    });
    // --- api --- //
    const api = useMemo(() => ({
        get: getRenderer,
        resetCamera,
        requestRender: () => {
            if (autoResetCamera) {
                resetCamera();
            }
            renderWindow.requestRender();
        },
    }), [autoResetCamera, renderWindow, resetCamera, getRenderer]);
    useEffect(() => {
        if (modifiedRef.current) {
            api.requestRender();
            resetModified();
        }
    });
    useImperativeHandle(fwdRef, () => api);
    return (jsx(RendererContext.Provider, Object.assign({ value: api }, { children: props.children })));
});

export { DefaultProps, Renderer as default };
